import { createAsyncThunk } from "@reduxjs/toolkit";
import {
    ICancelTimeoffRequest,
    ICreateTimeOffRequest,
    IGetTimeOffBalance,
    IGetTimeOffRequestList,
    IRejectWithValue,
    IUpdateTimeoffRequest,
    PhpResponse,
    Timeoff,
    TimeoffBalance,
    TimeoffCategoryType,
    TimeoffEarningCodeType,
    TimeoffPolicy,
    TimeoffRequest,
} from "../../../types";
import { phpClient } from "../../../network";
import {
    CreateTimeoffRuleRequestType,
    GetTimeoffPolicyListRequest,
    IGetTimeOffRequestDetails,
    UpdateTimeOffRuleRequest,
} from "../../../types/request/timeoff";
import { time } from "node:console";

export const getTimeoffPolicyList = createAsyncThunk<TimeoffPolicy[], GetTimeoffPolicyListRequest, IRejectWithValue>(
    "timeoff/getTimeoffPolicyList",
    async (data, { getState, rejectWithValue }) => {
        try {
            const response = await phpClient.post<PhpResponse<TimeoffPolicy[]>>({
                url: "/timeoff/timeoff_policy_list",
                data,
            });
            return response.data ?? [];
        } catch (error: any) {
            return rejectWithValue(error.message || "An error occurred");
        }
    }
);

export const getTimeoffCategoryList = createAsyncThunk<TimeoffCategoryType[], void, IRejectWithValue>(
    "timeoff/getTimeoffCategoryList",
    async (data, { rejectWithValue }) => {
        try {
            const response = await phpClient.get<TimeoffCategoryType[]>({
                url: "/timeoff/list_assigned_category",
            });

            return response;
        } catch (error: any) {
            return rejectWithValue(error.message || "An error occurred");
        }
    }
);

export const createTimeOffRequest = createAsyncThunk<void, ICreateTimeOffRequest, IRejectWithValue>(
    "timeoff/createTimeOffRequest",
    async (data, { rejectWithValue }) => {
        try {
            const response = await phpClient.post<void>({
                url: "/timeoff/create_timeoff_request",
                data,
            });
            return response;
        } catch (error: any) {
            return rejectWithValue(error.message || "An error occurred");
        }
    }
);

export const getTimeOffRequestList = createAsyncThunk<Timeoff[], IGetTimeOffRequestList, IRejectWithValue>(
    "timeoff/getTimeOffRequestList",
    async (data, { rejectWithValue }) => {
        try {
            const response = await phpClient.post<Timeoff[]>({
                url: "/timeoff/timeoff_request_list",
                data,
            });
            return response;
        } catch (error: any) {
            return rejectWithValue(error.message || "An error occurred");
        }
    }
);

export const getTimeoffRequestDetails = createAsyncThunk<TimeoffRequest | undefined, IGetTimeOffRequestDetails, IRejectWithValue>(
    "timeoff/getTimeOffRequestDetails",
    async (data, { rejectWithValue }) => {
        try {
            const { time_off_request_id } = data;
            const response = await phpClient.get<PhpResponse<TimeoffRequest | undefined>>({
                url: `/timeoff/timeoff_request_details/${time_off_request_id}`,
            });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.message || "An error occurred");
        }
    }
);

export const getBalanceByCategory = createAsyncThunk<TimeoffBalance[], IGetTimeOffBalance, IRejectWithValue>(
    "timeoff/getBalanceByCategory",
    async (data, { rejectWithValue }) => {
        try {
            const response = await phpClient.post<PhpResponse<TimeoffBalance[]>>({
                url: "/timeoff/fetch_timeoff_balance",
                data,
            });

            return response.data ?? [];
        } catch (error: any) {
            return rejectWithValue(error.message || "An error occurred");
        }
    }
);

export const cancelTimeOffRequest = createAsyncThunk<void, ICancelTimeoffRequest, IRejectWithValue>(
    "timeoff/cancelTimeOffRequest",
    async (data, { rejectWithValue }) => {
        const { request_id } = data;
        try {
            const response = await phpClient.get<void>({
                url: `/timeoff/delete_timeoff_request/${request_id}`,
            });
            return response;
        } catch (error: any) {
            return rejectWithValue(error.message || "An error occurred");
        }
    }
);

export const updateTimeoffRequest = createAsyncThunk<void, IUpdateTimeoffRequest, IRejectWithValue>(
    "timeoff/updateTimeOffRequest", // Corrected the action name
    async (data, { rejectWithValue }) => {
        const { time_off_request_id, ...request } = data;
        try {
            const response = await phpClient.post<void>({
                url: `/timeoff/update_timeoff_request/${time_off_request_id}`,
                data: request,
            });
           
        } catch (error: any) {
            return rejectWithValue(error.message || "An error occurred");
        }
    }
);

export const createTimeoffPolicy = createAsyncThunk<PhpResponse<any>, CreateTimeoffRuleRequestType, IRejectWithValue>(
    "timeoff/createTimeoffPolicy",
    async (data, { rejectWithValue }) => {
        try {
            const response = await phpClient.post<PhpResponse<any>>({
                url: "/timeoff/create_timeoff_policy",
                data,
            });
            return response; // Ensure the correct response is returned
        } catch (error: any) {
            return rejectWithValue(error.message || "An error occurred");
        }
    }
);

export const updateTimeoffPolicy = createAsyncThunk<PhpResponse<any>, UpdateTimeOffRuleRequest, IRejectWithValue>(
    "timeoff/updateTimeoffPolicy",
    async (data, { rejectWithValue }) => {
        try {
            const response = await phpClient.post<PhpResponse<any>>({
                url: `/timeoff/update_timeoff_policy/${data.policy_code}`, // Ensure policy_code is correctly used
                data,
            });
            return response; // Ensure the correct response is returned
        } catch (error: any) {
            return rejectWithValue(error.message || "An error occurred");
        }
    }
);

export const getTimeoffEarningCodes = createAsyncThunk<TimeoffEarningCodeType[], void, IRejectWithValue>(
    "timeoff/getTimeoffTypes",
    async (data, { rejectWithValue }) => {
        try {
            const response = await phpClient.get<PhpResponse<TimeoffEarningCodeType[]>>({
                url: "/timeoff/timeoff_type",
            });
            return response.data ?? [];
        } catch (error: any) {
            return rejectWithValue(error.message || "An error occurred");
        }
    }
);


